import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  OVERTIME_TYPE_VALUES,
  calculateOvertimeAmount,
  calculateHourlyRateFromSalary,
  SAUDI_OVERTIME_RATE,
  SAUDI_MONTHLY_HOURS_DIVISOR,
  normalizeOvertimeType,
} from '../pointage/pointageHelpers';
import {
  calculateHourlyRateFromBasic,
  calculateSaudiOvertimePay,
  SAUDI_OVERTIME_RATE as PAYROLL_OT_RATE,
} from './utils/payrollSaudiFormat';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertCircle,
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, FileText,
  Filter, Eye, X, XCircle, LayoutGrid, Layers, Users, DollarSign,
  Shield, Banknote, User, Info,
} from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  SalairesListControlsStrip,
  useClearSearchOnTableView,
  renderSalairesBulkDeleteFilterAction,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import '../../core/styles/global-dropdowns.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const HeuresSupplementairesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.heuresSupplementairesPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return p('not_available');
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('not_available');
    return formatBrandMoney(n);
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('not_available'));
  const { isDashboardLightMode } = useGlobalTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_overtime', { defaultValue: 'ساعة إضافية' });

  const unwrapApiList = (response) => {
    if (Array.isArray(response)) return response;
    if (response?.success && Array.isArray(response.data)) return response.data;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    date: '',
    heures: '',
    typeHeure: '',
    montantBase: '',
    notes: '',
    status: '',
  });

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_overtime');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const [heuresSupplementaires, setHeuresSupplementaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const { employees } = useSalairesEmployeeOptions();

  const fetchData = async () => {
    try {
      setLoading(true);
      const overtimeRes = await localApiAdapter.get('/salaries/overtime');
      setHeuresSupplementaires(sortRecordsNewestFirst(unwrapApiList(overtimeRes)));
    } catch (error) {
      console.error('Error fetching overtime:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const typeHeuresOptions = [
    { value: OVERTIME_TYPE_VALUES.regular, label: p('type_regular'), shortLabel: p('type_regular_short'), majoration: SAUDI_OVERTIME_RATE },
    { value: OVERTIME_TYPE_VALUES.friday, label: p('type_friday'), shortLabel: p('type_friday_short'), majoration: SAUDI_OVERTIME_RATE },
    { value: OVERTIME_TYPE_VALUES.holiday, label: p('type_holiday'), shortLabel: p('type_holiday_short'), majoration: SAUDI_OVERTIME_RATE },
    { value: OVERTIME_TYPE_VALUES.night, label: p('type_night'), shortLabel: p('type_night_short'), majoration: SAUDI_OVERTIME_RATE },
  ];

  const resolveHourlyRate = (employeeMat, explicitBase) => {
    if (explicitBase != null && explicitBase !== '' && !Number.isNaN(parseFloat(explicitBase))) {
      return parseFloat(explicitBase);
    }
    const emp = employees.find((e) => e.mat === employeeMat);
    const basic = emp?.salaireBase ?? emp?.basic_salary ?? emp?.salary;
    return calculateHourlyRateFromBasic(basic) || calculateHourlyRateFromSalary(basic);
  };

  const estimateOvertimePay = (hours, hourlyRate, hourType) => {
    const normalized = normalizeOvertimeType(hourType);
    return calculateOvertimeAmount(hours, hourlyRate, normalized)
      || calculateSaudiOvertimePay(hours, hourlyRate, PAYROLL_OT_RATE);
  };

  const stats = {
    total: heuresSupplementaires.length,
    validated: heuresSupplementaires.filter((h) => h.status === 'validated').length,
    pending: heuresSupplementaires.filter((h) => h.status === 'pending').length,
    totalHeures: heuresSupplementaires.reduce((sum, h) => sum + (Number(h.hours) || 0), 0),
    montantTotal: heuresSupplementaires
      .filter((h) => h.status === 'validated')
      .reduce((sum, h) => sum + (Number(h.majorated_amount) || 0), 0),
  };

  const mainTabs = [
    { id: 'all_overtime', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_statut', name: t('common.by_status'), icon: Shield },
    { id: 'par_type', name: t('common.by_type'), icon: Clock },
    { id: 'par_employe', name: p('tab_by_employee'), icon: Users },
  ];

  const quickFilterButtons = [
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'validated', name: p('filter_validated'), icon: CheckCircle },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'rejected', name: p('filter_rejected'), icon: XCircle },
  ];

  const getTypeDistribution = () =>
    typeHeuresOptions.map((t) => ({
      filterKey: t.value,
      name: t.shortLabel,
      count: heuresSupplementaires.filter((h) => h.hour_type === t.value).length,
      color: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
    }));

  const overtimeDistribution = {
    all_overtime: {
      title: p('overview_overtime'),
      data: [
        { filterKey: 'validated', name: p('dist_validated'), count: stats.validated, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'rejected', name: p('dist_rejected'), count: heuresSupplementaires.filter((h) => h.status === 'rejected').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'total_hours', name: p('dist_total_hours'), count: Math.round(stats.totalHeures), color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { filterKey: 'validated', name: p('dist_validated'), count: stats.validated, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'rejected', name: p('dist_rejected'), count: heuresSupplementaires.filter((h) => h.status === 'rejected').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_type: {
      title: p('distribution_by_type'),
      data: getTypeDistribution(),
    },
    par_employe: {
      title: p('distribution_by_employee'),
      data: (() => {
        const counts = {};
        heuresSupplementaires.forEach((h) => {
          const name = h.employee_name || h.employee_mat || p('undefined_employee');
          counts[name] = (counts[name] || 0) + 1;
        });
        return Object.entries(counts)
          .slice(0, 4)
          .map(([name, count]) => ({
            filterKey: name,
            name,
            count,
            color: 'bg-gradient-to-r from-blue-500 to-blue-600',
          }));
      })(),
    },
  };

  const distributionInfo = overtimeDistribution[mainViewMode] || overtimeDistribution.all_overtime;

  const statusOptions = [
    { value: 'validated', label: p('status_validated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('employee', (row) => `${row.employee_name || ''} ${row.employee_mat || ''}`.trim()),
      textColumnRule('date', (row) => fmtDate(row.date)),
      textColumnRule('hours', (row) => String(row.hours ?? '')),
      textColumnRule('montant', (row) => String(row.majorated_amount ?? '')),
      exactColumnRule('status', (row) => row.status || ''),
    ],
    [dateLocale],
  );

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const filteredData = useMemo(() => {
    let result = heuresSupplementaires.filter((item) => {
      if (
        searchTerm &&
        !item.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !item.employee_mat?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      if (mainViewMode === 'all_overtime') {
        if (quickFilter === 'validated' && item.status !== 'validated') return false;
        if (quickFilter === 'pending' && item.status !== 'pending') return false;
        if (quickFilter === 'rejected' && item.status !== 'rejected') return false;
      }

      if (activeDistributionFilter) {
        switch (mainViewMode) {
          case 'all_overtime':
          case 'par_statut':
            if (activeDistributionFilter === 'validated' && item.status !== 'validated') return false;
            if (activeDistributionFilter === 'pending' && item.status !== 'pending') return false;
            if (activeDistributionFilter === 'rejected' && item.status !== 'rejected') return false;
            if (activeDistributionFilter === 'total_hours') return true;
            break;
          case 'par_type':
            if (activeDistributionFilter && item.hour_type !== activeDistributionFilter) return false;
            break;
          case 'par_employe': {
            const empName = item.employee_name || item.employee_mat || p('undefined_employee');
            if (activeDistributionFilter && empName !== activeDistributionFilter) return false;
            break;
          }
          default:
            break;
        }
      }

      return true;
    });

    return applyColumnFilters(result, columnFilters, columnFilterRules);
  }, [heuresSupplementaires, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredData);

  const handleAdd = () => {
    setEditingRecord(null);
    setFormData({
      employeeId: '',
      date: '',
      heures: '',
      typeHeure: '',
      montantBase: '',
      notes: '',
      status: '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;

    try {
      const result = await localApiAdapter.delete(`/salaries/overtime/${id}`);
      if (result?.success === false) {
        notifyError(showNotify, result.error || p('save_error'));
        return;
      }
      notifyDeleted(showNotify, entityLabel);
      await fetchData();
    } catch (error) {
      console.error('Error deleting overtime:', error);
      notifyError(showNotify, p('network_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(
        selectedRecords.map((id) => localApiAdapter.delete(`/salaries/overtime/${id}`))
      );
      clearSelection();
      notifyBulkDeleted(showNotify, deleteCount, entityLabel);
      await fetchData();
    } catch (error) {
      console.error('Error bulk deleting overtime:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormData({
      employeeId: '',
      date: '',
      heures: '',
      typeHeure: '',
      montantBase: '',
      notes: '',
      status: '',
    });
  };

  const handleEdit = (item) => {
    setEditingRecord(item);
    setFormData({
      employeeId: item.employee_mat || item.employee_id || '',
      date: item.date || '',
      heures: item.hours != null && item.hours !== '' ? String(item.hours) : '',
      typeHeure: item.hour_type ?? '',
      montantBase: item.base_amount != null && item.base_amount !== '' ? String(item.base_amount) : '',
      notes: item.notes || '',
      status: item.status || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const h = parseFloat(formData.heures);
    const base = parseFloat(formData.montantBase);
    if (!formData.employeeId?.trim() || !formData.date || Number.isNaN(h) || h < 0) {
      notifyError(showNotify, p('validation_employee_date_hours'));
      return;
    }
    if (formData.montantBase === '' || Number.isNaN(base) || base < 0) {
      notifyError(showNotify, p('validation_base_amount'));
      return;
    }
    if (!formData.typeHeure?.trim()) {
      notifyError(showNotify, p('validation_hour_type'));
      return;
    }

    try {
      const payload = {
        employee_id: formData.employeeId,
        date: formData.date,
        hours: h,
        hour_type: formData.typeHeure,
        status: formData.status || 'pending',
        notes: formData.notes || '',
      };

      const result = editingRecord
        ? await localApiAdapter.put(`/salaries/overtime/${editingRecord.id}`, payload)
        : await localApiAdapter.post('/salaries/overtime', payload);

      if (result?.success === false) {
        notifyError(showNotify, result.error || result.message || p('save_error'));
        return;
      }

      if (editingRecord) notifyUpdated(showNotify, entityLabel);
      else notifyCreated(showNotify, entityLabel);
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving overtime:', error);
      notifyError(showNotify, error.message || p('network_error'));
    }
  };

  const getTypeLabel = (value) =>
    typeHeuresOptions.find((t) => t.value === value)?.label || value;

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[1];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const item = heuresSupplementaires.find((h) => h.id === recordId);
      if (!item) return;
      await localApiAdapter.put(`/salaries/overtime/${recordId}`, { ...item, status: newStatus });
      await fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const employeeSelectOptions = employees.map((emp) => ({
    value: String(emp.id ?? emp.mat),
    label: `${emp.prenom || ''} ${emp.nom || ''}`.trim() + (emp.mat ? ` (${emp.mat})` : ''),
  }));

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'employee', label: t('common.employee'), required: true },
    { key: 'date', label: t('common.date') },
    { key: 'hours', label: p('col_hours') },
    { key: 'hour_type', label: t('common.type') },
    { key: 'base_amount', label: p('col_base_amount') },
    { key: 'montant', label: p('col_majorated_amount') },
    { key: 'status', label: t('common.status') },
  ]), [p, t]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Clock className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={fetchData} className="refresh-button">
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
          <p className="text-white/50 text-sm mt-1">{p('saudi_law_hint', { rate: '150%', divisor: SAUDI_MONTHLY_HOURS_DIVISOR })}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={handleAdd}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{t('common.add')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('kpi_total_records'), value: stats.total, icon: Clock, color: 'from-blue-500 to-blue-600' },
          { title: p('kpi_validated'), value: stats.validated, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('kpi_pending'), value: stats.pending, icon: AlertCircle, color: 'from-yellow-500 to-yellow-600' },
          { title: p('kpi_total_hours'), value: `${stats.totalHeures}${p('hours_suffix')}`, icon: Clock, color: 'from-purple-500 to-purple-600' },
          { title: p('kpi_total_amount'), value: formatAmountDisplay(stats.montantTotal), icon: DollarSign, color: 'from-orange-500 to-orange-600' },
        ].slice(0, 4).map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={false}
        animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setMainViewMode(tab.id);
                    setQuickFilter('all');
                    setActiveDistributionFilter(null);
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {mainViewMode === 'all_overtime' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filterBtn) => (
                  <button
                    key={filterBtn.id}
                    onClick={() => setQuickFilter(filterBtn.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filterBtn.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <filterBtn.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filterBtn.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button
                  key={item.filterKey || item.name}
                  onClick={() =>
                    setActiveDistributionFilter(
                      activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name)
                    )
                  }
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>
                      {item.name}
                    </h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div
                      className={`h-3 rounded-full ${item.color} transition-all`}
                      style={{
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                        })()}%`,
                      }}
                    />
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>
                    {p('percent_of_total', {
                      percent: (() => {
                        const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                        return total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      })(),
                    })}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        <SalairesListControlsStrip
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
          isDashboardLightMode={isDashboardLightMode}
          cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
          tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
          filterActions={renderSalairesBulkDeleteFilterAction({
            selectedCount: selectedRecords.length,
            onBulkDelete: handleBulkDelete,
            t,
          })}
        />

        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('employee') ? <TableColumnHeaderFilter {...colHeaderFilterProps('employee', t('common.employee'))} /> : null}
                  {showCol('date') ? <TableColumnHeaderFilter {...colHeaderFilterProps('date', t('common.date'))} /> : null}
                  {showCol('hours') ? <TableColumnHeaderFilter {...colHeaderFilterProps('hours', p('col_hours'))} /> : null}
                  {showCol('hour_type') ? <th className="px-6 py-4 text-left text-white font-semibold">{t('common.type')}</th> : null}
                  {showCol('base_amount') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_base_amount')}</th> : null}
                  {showCol('montant') ? <TableColumnHeaderFilter {...colHeaderFilterProps('montant', p('col_majorated_amount'))} /> : null}
                  {showCol('status') ? (
                  <TableColumnHeaderFilter
                    {...colHeaderFilterProps('status', t('common.status'), {
                      align: 'center',
                      kind: 'select',
                      options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                    })}
                  />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12">
                      <RefreshCcw size={48} className="mx-auto mb-4 text-white/30 animate-spin" />
                      <p className="text-white/50">{p('loading')}</p>
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(item.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(item.id)}
                        onChange={() => handleSelectRecord(item.id)}
                      />
                      {showCol('employee') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{item.employee_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{item.employee_mat}</span>
                        </div>
                      </td>
                      ) : null}
                      {showCol('date') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">
                          {fmtDate(item.date)}
                        </span>
                      </td>
                      ) : null}
                      {showCol('hours') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{item.hours}{p('hours_suffix')}</span>
                      </td>
                      ) : null}
                      {showCol('hour_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{getTypeLabel(item.hour_type)}</span>
                      </td>
                      ) : null}
                      {showCol('base_amount') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-bold text-lg">
                          {formatAmountDisplay(item.base_amount)}
                        </span>
                      </td>
                      ) : null}
                      {showCol('montant') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-bold text-lg">
                          {formatAmountDisplay(item.majorated_amount)}
                        </span>
                      </td>
                      ) : null}
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={item.status}
                            recordId={item.id}
                            options={statusOptions}
                            onChange={handleStatusChange}
                            title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => {
                            setViewingRecord(item);
                            setShowViewModal(true);
                          }}
                          onEdit={() => handleEdit(item)}
                          onDelete={() => handleDelete(item.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12 text-white/60">
                      <Clock size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_records')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_records_count', { count: filteredData.length })}</span>
                      <span>
                        {p('footer_total_hours')}{' '}
                        <span className="text-white font-semibold">
                          {filteredData.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0).toFixed(1)}{p('hours_suffix')}
                        </span>
                      </span>
                      <span>
                        {p('footer_total_majorated')}{' '}
                        <span className="text-white font-semibold">
                          {formatAmountDisplay(
                            filteredData
                              .filter((item) => item.status === 'validated')
                              .reduce((sum, item) => sum + (parseFloat(item.majorated_amount) || 0), 0)
                          )}
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full text-center py-12">
                <RefreshCcw size={48} className="mx-auto mb-4 text-white/30 animate-spin" />
                <p className="text-white/50">{t('common.loading')}</p>
              </div>
            ) : filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Clock size={20} className="text-emerald-400" />
                          {item.employee_name}
                        </h3>
                        <div className="flex">
                          <QawaemClickableStatusBadge status={item.status} recordId={item.id} options={statusOptions} onChange={handleStatusChange} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_employee_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <User size={14} className="text-blue-400 shrink-0" />
                              <span className="font-medium">{p('label_matricule')}</span> {item.employee_mat}
                            </div>
                            <div>
                              <span className="font-medium">{t('common.date')}:</span>{' '}
                              {fmtDate(item.date)}
                            </div>
                            <div>
                              <span className="font-medium">{p('label_hours')}</span>{' '}
                              <span className="text-white font-bold">{item.hours}{p('hours_suffix')}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_type_config')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>
                              <span className="font-medium">{t('common.type')}:</span> {getTypeLabel(item.hour_type)}
                            </div>
                            <div>
                              <span className="font-medium">{p('label_majoration')}</span>{' '}
                              {typeHeuresOptions.find((t) => t.value === item.hour_type)?.majoration || 1}x
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>
                              <span className="font-medium">{p('label_base')}</span>{' '}
                              <span className="text-white font-bold">
                                {formatAmountDisplay(item.base_amount)}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-green-400">{p('label_majorated')}</span>{' '}
                              <span className="text-white font-bold text-lg">
                                {formatAmountDisplay(item.majorated_amount)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_status_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {item.notes && (
                              <div>
                                <span className="font-medium">{p('label_notes')}</span> {item.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => {
                        setViewingRecord(item);
                        setShowViewModal(true);
                      }}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <Clock size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_records')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open={showViewModal}
        onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
        title={p('view_title')}
        icon={Eye}
        testId="heures-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <User size={18} className="text-emerald-400" />
                    {p('view_employee_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('common.employee')}</p>
                      <p className="text-white font-medium">{viewingRecord.employee_name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_matricule').replace(':', '')}</p>
                      <p className="text-white font-medium">{viewingRecord.employee_mat}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('common.date')}</p>
                      <p className="text-white font-medium">
                        {fmtDate(viewingRecord.date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_hours')}</p>
                      <p className="text-white font-medium">{viewingRecord.hours}{p('hours_suffix')}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('view_type_majoration')}</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('view_hour_type')}</p>
                        <p className="text-white font-medium">{getTypeLabel(viewingRecord.hour_type)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('view_coefficient')}</p>
                        <p className="text-white font-medium">
                          {typeHeuresOptions.find((t) => t.value === viewingRecord.hour_type)?.majoration || 1}x
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Banknote size={18} className="text-green-400" />
                      {p('view_amounts')}
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                        <p className="text-white/60 text-sm mb-1">{p('view_base_amount')}</p>
                        <p className="text-white font-bold text-2xl">
                          {formatAmountDisplay(viewingRecord.base_amount)}
                        </p>
                      </div>
                      <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/20">
                        <p className="text-white/60 text-sm mb-1">{p('view_majorated_amount')}</p>
                        <p className="text-emerald-400 font-bold text-2xl">
                          {formatAmountDisplay(viewingRecord.majorated_amount)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4">{t('common.status')}</h3>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(viewingRecord.status)}
                    {viewingRecord.notes && (
                      <p className="text-white/70 text-sm">{viewingRecord.notes}</p>
                    )}
                  </div>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`}
            style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingRecord ? p('modal_edit') : p('modal_add')}
              </h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="heures-sup-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button type="button" onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="heures-sup-form" onSubmit={handleSubmit}>
                <div className={SALAIRES_FORM_GRID_CLASS}>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}><RequiredLabelText text={p('employee_required')} required /></label>
                    <CustomSelect value={formData.employeeId} onChange={(value) => { const rate = resolveHourlyRate(value, ''); setFormData({ ...formData, employeeId: value, montantBase: rate > 0 ? String(Math.round(rate * 100) / 100) : formData.montantBase }); }} options={employeeSelectOptions} placeholder={p('employee_placeholder')} autoSelect={false} searchable creatable />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}><RequiredLabelText text={p('date_required')} required /></label>
                    <CustomDatePicker value={formData.date} onChange={(v) => setFormData({ ...formData, date: v })} placeholder={t('common.select')} />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}><RequiredLabelText text={p('hours_required')} required /></label>
                    <CustomNumberInput required min={0} step={0.5} decimals={1} value={formData.heures} onChange={(v) => setFormData({ ...formData, heures: v })} placeholder="3" className="w-full" />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}><RequiredLabelText text={p('hour_type_required')} required /></label>
                    <CustomSelect value={formData.typeHeure} onChange={(value) => setFormData({ ...formData, typeHeure: value })} options={typeHeuresOptions.map(({ value, label }) => ({ value, label }))} placeholder={p('hour_type_placeholder')} autoSelect={false} searchable creatable />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}><RequiredLabelText text={p('base_amount_required')} required /></label>
                    <CustomNumberInput required min={0} step={0.01} decimals={2} value={formData.montantBase} onChange={(v) => setFormData({ ...formData, montantBase: v })} placeholder="62.50" className="w-full" />
                  </div>
                  <div className="min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('status_readonly')}</label>
                    <CustomSelect value={formData.status || 'pending'} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map((s) => ({ value: s.value, label: s.label, icon: <s.icon size={14} className={s.style.split(' ')[1]} /> }))} placeholder={t('common.status')} autoSelect={false} searchable creatable disabled={!editingRecord} />
                  </div>
                  <div className="md:col-span-3 min-w-0">
                    <label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('notes_label')}</label>
                    <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder={p('notes_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" />
                  </div>
                  <div className="md:col-span-3 min-w-0">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <p className={`text-sm mb-2 ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('calc_info')}</p>
                      {formData.montantBase && formData.typeHeure && formData.heures && (
                        <p className="text-blue-400 font-semibold">
                          {p('calc_majoration')} {typeHeuresOptions.find((t) => t.value === formData.typeHeure)?.majoration || SAUDI_OVERTIME_RATE}x — {p('calc_estimate')} {formatAmountDisplay(estimateOvertimePay(parseFloat(formData.heures), parseFloat(formData.montantBase), formData.typeHeure))}
                        </p>
                      )}
                    </div>
                    <p className={`text-sm mt-3 ${isDashboardLightMode ? 'text-gray-500' : 'text-white/50'}`}>{p('calc_pending_note')}</p>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default HeuresSupplementairesPage;

